import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield, AlertTriangle, RefreshCw, Search, LogIn, LogOut, UserPlus,
  ShieldOff, Loader2, Globe, Filter,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  login_success:        { label: 'Login',              tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: LogIn },
  login_failure:        { label: 'Failed login',       tone: 'bg-rose-500/15 text-rose-300 border-rose-500/30',          Icon: AlertTriangle },
  signup:               { label: 'Signup',             tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30',             Icon: UserPlus },
  logout:               { label: 'Logout',             tone: 'bg-slate-500/15 text-slate-300 border-slate-500/30',       Icon: LogOut },
  session_revoke_all:   { label: 'Revoked own sessions', tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30',     Icon: ShieldOff },
  admin_session_revoke: { label: 'Admin force-revoke', tone: 'bg-violet-500/15 text-violet-300 border-violet-500/30',    Icon: Shield },
};

const HOUR_WINDOWS = [
  { value: '1',   label: 'Last hour' },
  { value: '24',  label: 'Last 24h' },
  { value: '168', label: 'Last 7d' },
  { value: '720', label: 'Last 30d' },
];

function formatTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function EventBadge({ type }) {
  const meta = EVENT_META[type] || {
    label: type, tone: 'bg-slate-500/15 text-slate-300 border-slate-500/30', Icon: Shield,
  };
  const Icon = meta.Icon;
  return (
    <Badge variant="outline" className={`gap-1 border ${meta.tone}`} data-testid={`security-event-badge-${type}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  );
}

export default function AdminSecurityLog({ token }) {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState('24');
  const [eventType, setEventType] = useState('all');
  const [emailFilter, setEmailFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  const authHeaders = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/security-log/stats`, {
        params: { hours },
        headers: authHeaders(),
      });
      setStats(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load security stats';
      toast.error(msg);
    }
  }, [hours, authHeaders]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { hours, limit: 100 };
      if (eventType && eventType !== 'all') params.event_type = eventType;
      if (emailFilter.trim()) params.email = emailFilter.trim();
      if (ipFilter.trim()) params.ip = ipFilter.trim();

      const res = await axios.get(`${API}/admin/security-log`, {
        params, headers: authHeaders(),
      });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load audit log';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [hours, eventType, emailFilter, ipFilter, authHeaders]);

  useEffect(() => { loadStats(); loadEvents(); }, [loadStats, loadEvents]);

  const revokeUserSessions = async (userId, email) => {
    if (!window.confirm(`Revoke ALL sessions for ${email || userId}?`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/users/${userId}/revoke-sessions`,
        {},
        { headers: authHeaders() },
      );
      toast.success(res.data?.message || 'Sessions revoked');
      loadEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    }
  };

  const byType = stats?.by_event_type || {};
  const totalEvents = Object.values(byType).reduce((a, b) => a + b, 0);
  const failedLogins = byType.login_failure || 0;
  const revokes = (byType.session_revoke_all || 0) + (byType.admin_session_revoke || 0);

  return (
    <div className="space-y-6" data-testid="admin-security-log">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total events"    value={totalEvents} tone="from-amber-500/20 to-transparent" />
        <StatTile label="Failed logins"   value={failedLogins} tone="from-rose-500/20 to-transparent" testId="stat-failed-logins" />
        <StatTile label="Session revokes" value={revokes}     tone="from-violet-500/20 to-transparent" />
        <StatTile label="Unique IPs"      value={stats?.unique_ips || 0} tone="from-emerald-500/20 to-transparent" />
      </div>

      {/* Top offenders */}
      {(stats?.top_failed_ips?.length > 0 || stats?.top_failed_emails?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <OffenderCard
            title="Top failed-login IPs"
            icon={Globe}
            rows={stats.top_failed_ips.map((r) => ({ label: r.ip, count: r.count }))}
          />
          <OffenderCard
            title="Top failed-login emails"
            icon={AlertTriangle}
            rows={stats.top_failed_emails.map((r) => ({ label: r.email, count: r.count }))}
          />
        </div>
      )}

      {/* Filters + refresh */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-4 h-4 text-amber-500" />
            Security events
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadStats(); loadEvents(); }}
            className="border-slate-700"
            data-testid="security-log-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs text-slate-400">Window</Label>
              <Select value={hours} onValueChange={setHours}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="security-log-window-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOUR_WINDOWS.map((w) => (
                    <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Event type</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="security-log-event-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {Object.entries(EVENT_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Email contains</Label>
              <Input
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="user@example.com"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                data-testid="security-log-email-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">IP</Label>
              <Input
                className="bg-slate-800 border-slate-700 mt-1"
                placeholder="203.0.113.42"
                value={ipFilter}
                onChange={(e) => setIpFilter(e.target.value)}
                data-testid="security-log-ip-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={loadEvents}
              size="sm"
              className="bg-amber-500 text-black hover:bg-amber-400"
              data-testid="security-log-apply-filters"
            >
              <Search className="w-4 h-4 mr-2" />
              Apply filters
            </Button>
          </div>

          <div className="text-xs text-slate-400">
            Showing {events.length} of {total} matching events
          </div>

          <ScrollArea className="h-[560px] rounded-md border border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                No events for the selected filters.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 sticky top-0 z-10">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Event</th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">IP</th>
                    <th className="px-3 py-2">Detail</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((evt, idx) => (
                    <tr
                      key={`${evt.user_id || evt.email}-${evt.created_at}-${idx}`}
                      className="border-t border-slate-800 hover:bg-slate-800/40"
                      data-testid={`security-log-row-${idx}`}
                    >
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{formatTs(evt.created_at)}</td>
                      <td className="px-3 py-2"><EventBadge type={evt.event_type} /></td>
                      <td className="px-3 py-2 text-slate-200">
                        {evt.email || <span className="text-slate-500">—</span>}
                        {evt.user_id && (
                          <div className="text-[10px] text-slate-500 font-mono">{evt.user_id}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-300">{evt.ip || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-400 max-w-[280px] truncate">
                        {formatMetadata(evt.metadata)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {evt.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                            onClick={() => revokeUserSessions(evt.user_id, evt.email)}
                            data-testid={`security-log-revoke-${idx}`}
                            title="Force-revoke this user's sessions"
                          >
                            <ShieldOff className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({ label, value, tone, testId }) {
  return (
    <div
      className={`rounded-lg p-4 border border-slate-800 bg-gradient-to-b ${tone} bg-slate-900`}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white">{value}</div>
    </div>
  );
}

function OffenderCard({ title, icon: Icon, rows }) {
  if (!rows?.length) return null;
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-300">
          <Icon className="w-4 h-4 text-rose-400" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-sm">
            <span className="font-mono text-slate-300 truncate">{r.label}</span>
            <Badge variant="outline" className="border-rose-500/30 text-rose-300 bg-rose-500/10">
              {r.count}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function formatMetadata(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const pairs = Object.entries(meta)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`);
  return pairs.join(' · ');
}
